import { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../lib/api-client';

/**
 * Custom hook to handle photo blur logic based on 1:1 ratio + 1 free photo
 * User gets 1 free photo to view, then must upload X photos to see X+1 photos total
 * - 0 photos uploaded → can see 1 photo without blur
 * - 1 photo uploaded → can see 2 photos without blur
 * - 2 photos uploaded → can see 3 photos without blur
 * - And so on...
 */
export function usePhotoBlur() {
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch current user's full profile data to get photo count
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_URL}/profiles/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setCurrentUserProfile(data.data);
        }
      } catch (error) {
        // Silent fail in production
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching user profile for photo blur:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Calculate current user's photo count
  const currentUserPhotoCount = useMemo(() => {
    if (!currentUserProfile) return 0;
    
    // Check all possible photo locations
    let photos: any[] = [];
    
    if (currentUserProfile.photos && Array.isArray(currentUserProfile.photos)) {
      photos = currentUserProfile.photos;
    } else if (currentUserProfile.profile?.photos && Array.isArray(currentUserProfile.profile.photos)) {
      photos = currentUserProfile.profile.photos;
    } else if (currentUserProfile.profilePhoto) {
      photos = [currentUserProfile.profilePhoto];
    }
    
    // Filter out empty/invalid photos and count real uploaded photos
    const validPhotos = photos.filter((p: any) => {
      const photoUrl = typeof p === 'string' ? p : p?.url;
      if (!photoUrl || photoUrl.trim() === '') return false;
      
      // Only count real uploaded photos from CDN/S3
      const isRealPhoto = (
        !photoUrl.startsWith('data:') &&
        !photoUrl.startsWith('blob:') &&
        (photoUrl.includes('cloudfront.net') || 
         photoUrl.includes('amazonaws.com') ||
         photoUrl.includes('s3.') ||
         photoUrl.includes('.jpg') ||
         photoUrl.includes('.jpeg') ||
         photoUrl.includes('.png') ||
         photoUrl.includes('.webp'))
      );
      
      return isRealPhoto;
    });
    
    return validPhotos.length;
  }, [currentUserProfile]);

  /**
   * Calculate photo visibility for a given photo index
   * @param photoIndex - The index of the photo to check (0-based)
   * @returns Object with blur state and message
   */
  const getPhotoBlurState = (photoIndex: number) => {
    const shouldBlur = photoIndex >= currentUserPhotoCount + 1;
    
    let message = '';
    if (shouldBlur) {
      if (currentUserPhotoCount === 0) {
        message = 'Upload 1 photo to view more photos';
      } else {
        const photosNeeded = photoIndex - currentUserPhotoCount;
        message = `Upload ${photosNeeded} more to view more photos`;
      }
    }
    
    return {
      shouldBlur,
      message,
      currentUserPhotoCount
    };
  };

  /**
   * Calculate overall photo visibility (for first photo)
   */
  const calculatePhotoVisibility = () => {
    const canSeePhotos = currentUserPhotoCount > 0;
    
    return {
      canSeePhotos,
      currentUserPhotoCount,
      uploadMessage: currentUserPhotoCount === 0 
        ? 'Upload 1 photo to view photos' 
        : `You can view ${currentUserPhotoCount} photo${currentUserPhotoCount > 1 ? 's' : ''}`
    };
  };

  return {
    currentUserPhotoCount,
    loading,
    getPhotoBlurState,
    calculatePhotoVisibility
  };
}